import { inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@sama/app-state';
import { difference, get, isNil } from 'lodash';
import { combineLatest } from 'rxjs';
import {
  getLocationForFrameNumber,
  ShapeLocationState,
} from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { OrthographicShapeState3D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  objectNumberedKeys,
  truncateToPoint2D,
} from '../../../../cross-team-shared/misc-utils';
import { strictForOwn } from '../../../../cross-team-shared/misc-utils/strict-record';
import {
  Point3D,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { KdTreeService } from '../../../utils/kd-tree.service';
import { interpolateLocation } from '../../../utils/point-cloud-interpolation-helper';
import { shouldCurveEdges } from '../../../utils/projections/projection-utils';
import { AbstractMeshService } from '../abstract-mesh.service';
import { AbstractPolyShapeHelper } from './abstract-polyshape-helper';

@Injectable()
export abstract class AbstractPolyShapeService<
    T extends AbstractPolyShapeHelper,
  >
  extends AbstractMeshService<T>
  implements OnDestroy
{
  private kdTreeService = inject(KdTreeService);

  protected abstract shapeStore: Store<
    StrictRecord<number, OrthographicShapeState3D>
  >;

  setShape(shapeState: OrthographicShapeState3D) {
    const groundedShape = this.groundKeyLocations(shapeState);
    this.shapeStore(shapeState.index).set(groundedShape);
  }

  getSubPoints() {
    return this.getHelpers().flatMap((shape) => shape.getSubPoints());
  }

  protected setUpSubscriptions() {
    this.subscribeTo(
      combineLatest([
        this.shapeStore.$,
        this.renderStore('frameNumber').$,
        this.renderStore('shapeTransientInfo').$,
      ]),
      ([shapes, frameNumber]) => {
        this.updateShapes(shapes, frameNumber);
      },
    );
  }

  protected updateShapes(
    shapesStates: StrictRecord<number, OrthographicShapeState3D>,
    frameNumber: number,
  ) {
    const shapeIds = objectNumberedKeys(shapesStates);

    this.addShapes(difference(shapeIds, Array.from(this.helpers.keys())));
    this.removeShapes(difference(Array.from(this.helpers.keys()), shapeIds));

    this.updateShapesLocations(shapesStates, frameNumber);
  }

  private updateShapeLocation(
    shapeHelper: AbstractPolyShapeHelper,
    location: ShapeLocationState<Point3D>,
  ) {
    shapeHelper.update(location.points, shouldCurveEdges(this.store.state()));
  }

  private updateShapesLocations(
    shapeStates: StrictRecord<number, OrthographicShapeState3D>,
    frameNumber: number,
  ) {
    strictForOwn(shapeStates, (shapeState, index) => {
      const { key_locations } = shapeState;
      // we have to use the shapes record key instead of the shape's index
      // because training shapes all have the same index
      const shapeHelper = this.getHelper(+index);
      const location = interpolateLocation(key_locations, frameNumber);
      this.updateShapeLocation(shapeHelper, location);
    });
  }

  private addShapes(indexes: number[]) {
    indexes.forEach((index) => {
      this.addHelper(index, this.createHelper(index));
    });
  }

  private removeShapes(indexes: number[]) {
    indexes.forEach((index) => {
      this.removeHelper(index);
    });
  }

  private groundKeyLocations(draftState: OrthographicShapeState3D) {
    const shapeStateKeyLocations =
      this.shapeStore.state()[draftState.index]?.key_locations ?? [];

    const draftKeyLocations = get(draftState, 'key_locations', []);

    return {
      ...draftState,
      key_locations: draftState.key_locations.map((location) => {
        const savedStateLocation = getLocationForFrameNumber(
          shapeStateKeyLocations,
          location.frame_number,
        );

        const draftStateLocation: ShapeLocation<Point3D> | undefined =
          getLocationForFrameNumber(draftKeyLocations, location.frame_number);

        return this.getCorrectLocation(
          location,
          draftStateLocation,
          savedStateLocation,
        );
      }),
    };
  }

  private getCorrectLocation(
    location: ShapeLocationState<Point3D>,
    draftStateLocation: ShapeLocation<Point3D> | undefined,
    savedStateLocation: ShapeLocation<Point3D> | undefined,
  ) {
    let points: Point3D[] = [];
    const curFrameNumber = this.store.state().render.frameNumber;

    const isUndefined = isZComponentUndefined(draftStateLocation);

    if (!savedStateLocation && !isUndefined) {
      points = draftStateLocation!.points;
    } else if (
      location.frame_number !== curFrameNumber &&
      !!savedStateLocation
    ) {
      points = savedStateLocation.points;
    } else {
      const { searchRadius, useAllPoints } =
        this.store.state().render.layerSettings.groundingConfiguration;
      points = location.points.map((p) => [
        p[0],
        p[1],
        this.kdTreeService.getGroundZ(
          truncateToPoint2D(p),
          searchRadius,
          useAllPoints ? 'all' : 'ground',
        ),
      ]);
    }
    return { ...location, points };
  }

  protected abstract createHelper(index: number): T;
}

function isZComponentUndefined(
  shapeLocation: ShapeLocation<Point3D> | undefined,
) {
  return (
    isNil(get(shapeLocation, ['points', 0, 2], NaN)) ||
    isNaN(get(shapeLocation, ['points', 0, 2], NaN))
  );
}
